import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { css } from 'emotion';
import { LoadingButton, Modal } from '@spotify-internal/anchor-ds';
import { TextLink, FormGroup } from '@spotify-internal/encore-web';
import { useT } from 'client/i18n/hooks/useT';
import {
  ControlledFieldInput,
  ControlledFieldTextArea,
  ControlledFieldSelect,
  FieldLabel,
} from 'components/encore/form/Field';
import { ERROR_MESSAGES } from 'screens/SettingsScreen/components/SettingsForm/constants';
import { paths } from 'client/shared/Paths';
import { useLanguagesList } from 'client/i18n/hooks/useLanguagesList';
import { Form } from './styles';

const Capitalized = styled(FieldLabel)`
  &:first-letter {
    text-transform: uppercase;
  }
`;

export type FormData = {
  podcastName?: string;
  podcastDescription?: string;
  itunesCategory?: string;
  language?: string;
};

type Option = { label: string; value: string };
type MetadataFormProps = {
  podcastCategoryOptions: Option[];
  stationType: string;
  onSubmit: (data: FormData) => Promise<void>;
  defaultValues: FormData;
};
export const MetadataForm = ({
  onSubmit,
  podcastCategoryOptions,
  stationType,
  defaultValues,
}: MetadataFormProps) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    setError,
  } = useForm({ defaultValues });
  const t = useT();
  const sortedLanguagesList = useLanguagesList();
  const [globalError, setGlobalError] = useState<boolean>(false);

  return (
    <Form
      onSubmit={handleSubmit((data, e) => {
        if (e) {
          // this will prevent any other forms on the page from unintentionally submitted
          e.stopPropagation();
          e.preventDefault();
        }
        setGlobalError(false);
        return onSubmit(data).catch(({ message }) => {
          switch (message) {
            case ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED:
              setError('podcastName', {
                message: ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED,
              });
              break;
            default:
              setGlobalError(true);
              break;
          }
        });
      })}
    >
      <FormGroup>
        <Capitalized htmlFor="podcastName">
          {t(
            'PreDistributionModalScreen.MetadataForm.StationNameFieldLabel',
            '{stationType} name',
            'Station name field label',
            {
              stationType:
                stationType === 'show'
                  ? t(
                      'PreDistributionModal.ShowStationType',
                      'show',
                      'Distribution modal station type'
                    )
                  : t(
                      'PreDistributionModal.PodcastStationType',
                      'podcast',
                      'Distribution modal station type'
                    ),
            }
          )}
        </Capitalized>
        <ControlledFieldInput
          control={control}
          name="podcastName"
          maxCharacterLength={100}
          type="text"
          showCharacterCount={true}
          error={
            errors.podcastName?.message ===
            ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED
              ? {
                  message: (
                    <>
                      {t(
                        'PreDistributionModalScreen.MetadataForm.PodcastNameFieldErrorMessage',
                        "You're a VIP! Please reach out to our {supportTeamLink} to finish your setup.",
                        'Podcast name field error message',
                        {
                          supportTeamLink: (
                            <a
                              key="support-link"
                              href={paths.getHelpPage('newRequest')}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t(
                                'PreDistributionModalScreen.MetadataForm.PodcastNameFieldErrorSupportTeamLinkText',
                                'support team',
                                'Support team link text'
                              )}
                            </a>
                          ),
                        }
                      )}
                    </>
                  ),
                }
              : errors.podcastName
          }
          rules={{
            required: t(
              'PreDistributionModalScreen.MetadataForm.FieldRequiredMessage',
              "This can't be left empty",
              'Form field required message'
            ),
          }}
        />
      </FormGroup>
      <FormGroup>
        <Capitalized htmlFor="podcastDescription">
          {t(
            'PreDistributionModalScreen.MetadataForm.StationDescriptionFieldLabel',
            '{stationType} description',
            'Station description field label',
            {
              stationType:
                stationType === 'show'
                  ? t(
                      'PreDistributionModal.ShowStationType',
                      'show',
                      'Distribution modal station type'
                    )
                  : t(
                      'PreDistributionModal.PodcastStationType',
                      'podcast',
                      'Distribution modal station type'
                    ),
            }
          )}
        </Capitalized>
        <ControlledFieldTextArea
          control={control}
          className={css`
            margin-bottom: 0px;
          `}
          name="podcastDescription"
          maxLength={600}
          error={errors.podcastDescription}
          rules={{
            required: t(
              'PreDistributionModalScreen.MetadataForm.FieldRequiredMessage',
              "This can't be left empty",
              'Form field required message'
            ),
          }}
        />
      </FormGroup>
      <FormGroup>
        <Capitalized htmlFor="category-dropdown">
          {t(
            'PreDistributionModalScreen.MetadataForm.StationCategoryFieldLabel',
            '{stationType} category',
            'Station category field label',
            {
              stationType:
                stationType === 'show'
                  ? t(
                      'PreDistributionModal.ShowStationType',
                      'show',
                      'Distribution modal station type'
                    )
                  : t(
                      'PreDistributionModal.PodcastStationType',
                      'podcast',
                      'Distribution modal station type'
                    ),
            }
          )}
        </Capitalized>
        <ControlledFieldSelect
          control={control}
          name="itunesCategory"
          id="category-dropdown"
          placeholder={t(
            'PreDistributionModalScreen.MetadataForm.SelectorPlaceholder',
            'Choose one...',
            'Selector placeholder'
          )}
          options={podcastCategoryOptions}
          rules={{
            required: t(
              'PreDistributionModalScreen.MetadataForm.FieldRequiredMessage',
              "This can't be left empty",
              'Form field required message'
            ),
          }}
        />
      </FormGroup>
      <FormGroup>
        <Capitalized htmlFor="language-dropdown">
          {t(
            'PreDistributionModalScreen.MetadataForm.StationLanguageFieldLabel',
            '{stationType} language',
            'Station language field label',
            {
              stationType:
                stationType === 'show'
                  ? t(
                      'PreDistributionModal.ShowStationType',
                      'show',
                      'Distribution modal station type'
                    )
                  : t(
                      'PreDistributionModal.PodcastStationType',
                      'podcast',
                      'Distribution modal station type'
                    ),
            }
          )}
        </Capitalized>
        <ControlledFieldSelect
          control={control}
          name="language"
          id="language-dropdown"
          placeholder={t(
            'PreDistributionModalScreen.MetadataForm.SelectorPlaceholder',
            'Choose one...',
            'Selector placeholder'
          )}
          options={sortedLanguagesList}
          rules={{
            required: t(
              'PreDistributionModalScreen.MetadataForm.FieldRequiredMessage',
              "This can't be left empty",
              'Form field required message'
            ),
          }}
        />
      </FormGroup>
      {globalError && (
        <Modal.Error
          className={css`
            text-align: center;
          `}
        >
          <span
            className={css`
              display: block;
            `}
          >
            {t(
              'PreDistributionModalScreen.MetadataForm.ErrorMessage',
              'Something went wrong.',
              'Podcast metadata saving error message'
            )}
          </span>
          {t(
            'PreDistributionModalScreen.MetadataForm.TryAgainMessage',
            'Try again or visit {helpPageLink} for more assistance.',
            'Podcast metadata saving error message',
            {
              helpPageLink: (
                <TextLink key="help-link" href={paths.getHelpPage('home')}>
                  {paths.getHelpPage('home')}
                </TextLink>
              ),
            }
          )}
        </Modal.Error>
      )}
      <Modal.Footer
        className={css`
          text-align: center;
        `}
      >
        <LoadingButton
          type="submit"
          disabled={!!Object.keys(errors).length}
          isLoading={isSubmitting}
        >
          {t('Button.Continue', 'Continue', 'Continue button text')}
        </LoadingButton>
      </Modal.Footer>
    </Form>
  );
};
